import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { CRM_API, errMsg } from "./api";
import PageHeader from "./components/PageHeader";
import ServicesPricing from "./ServicesPricing";
import { useCrmAuth } from "./CrmAuth";

const CATEGORIES = ["Detailing", "Paint Correction", "Ceramic Coating", "Window Tint", "PPF", "PDR", "Protection & Add-Ons", "Headlights", "Odor Treatment", "Bed Liner", "Other"];
const inp = "w-full bg-white/5 border border-white/12 rounded-lg px-3 py-2 outline-none focus:border-[#1F5EFF] text-white text-sm";

export default function ServicesCatalog() {
  const { user } = useCrmAuth();
  const canManage = user?.role === "owner" || user?.role === "manager";
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [confirmHideLegacy, setConfirmHideLegacy] = useState(false);
  const [hidingLegacy, setHidingLegacy] = useState(false);
  const legacy = rows.filter((s) => !s.pricing_key && s.active);

  const hideLegacy = async () => {
    setHidingLegacy(true);
    let ok = 0, fail = 0;
    for (const s of legacy) {
      try { await CRM_API.patch(`/services/${s.id}`, { active: false }); ok++; }
      catch { fail++; }
    }
    setHidingLegacy(false);
    setConfirmHideLegacy(false);
    if (fail) toast.error(`Hidden ${ok}, failed ${fail}`);
    else toast.success(`${ok} legacy services hidden — history and materials untouched`);
    load();
  };
  const [editing, setEditing] = useState(null);

  const load = () => CRM_API.get("/services").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const grouped = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = rows.filter((r) => r.category === cat);
    return acc;
  }, {});

  const remove = async (id) => {
    if (!confirm("Delete this service?")) return;
    try { await CRM_API.delete(`/services/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = async (r) => {
    try { await CRM_API.patch(`/services/${r.id}`, { active: !r.active }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="crm-services-page">
      <PageHeader
        eyebrow="Catalog"
        title="Services"
        subtitle="Website services, class prices and packages — one place. Changes apply to the site instantly."
        right={canManage && (
          <button onClick={() => setAdding(true)} data-testid="btn-add-service"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors">
            <Plus className="w-4 h-4" /> Add service
          </button>
        )}
      />
      <div className="px-6 md:px-10 py-8 space-y-10">
        <ServicesPricing />

        <div className="pt-6 border-t border-white/10">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="mb-1 font-mono-alt text-[11px] text-white/50 tracking-widest uppercase">Internal catalog</div>
            {canManage && legacy.length > 0 && !confirmHideLegacy && (
              <button onClick={() => setConfirmHideLegacy(true)} data-testid="hide-legacy-btn"
                className="px-3 py-1.5 rounded-lg border border-amber-500/40 text-amber-300 text-xs hover:bg-amber-500/10 transition-colors">
                Hide legacy duplicates ({legacy.length})
              </button>
            )}
            {confirmHideLegacy && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/40" data-testid="hide-legacy-confirm-box">
                <span className="text-amber-300 text-xs">Hide {legacy.length} legacy services? They stay in history — you can re-show any of them later.</span>
                <button onClick={hideLegacy} disabled={hidingLegacy} data-testid="hide-legacy-confirm"
                  className="px-2.5 py-1 rounded bg-amber-500/80 hover:bg-amber-500 text-black text-xs font-medium disabled:opacity-40">
                  {hidingLegacy ? "…" : "Yes, hide"}
                </button>
                <button onClick={() => setConfirmHideLegacy(false)} className="px-2.5 py-1 text-white/50 hover:text-white text-xs" data-testid="hide-legacy-cancel">Cancel</button>
              </span>
            )}
          </div>
          <p className="text-white/35 text-xs mb-6 max-w-3xl">
            Auto-synced from the website services above (name, base price, duration). Used for estimates,
            invoices, work orders and material consumption. Edit here only taxable status and materials —
            prices and names follow the website catalog on every save.
          </p>
        </div>
        {adding && <EditRow onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
        {CATEGORIES.map((cat) => (
          <section key={cat}>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-6 h-px bg-[#1F5EFF]" />
              <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">{cat}</span>
              <span className="text-white/30 text-xs">· {grouped[cat].length}</span>
            </div>
            {grouped[cat].length === 0 ? (
              <div className="text-white/30 text-xs italic">No services in this category.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {grouped[cat].map((r) => editing === r.id ? (
                  <EditRow key={r.id} initial={r} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
                ) : (
                  <div key={r.id} data-testid={`service-${r.id}`}
                    className={`p-4 rounded-xl border transition-all ${r.active ? "bg-white/5 border-white/10" : "bg-white/2 border-white/5 opacity-60"}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="text-white font-medium text-[15px] leading-snug">
                        {r.name}
                        {r.pricing_key && (
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-[#1F5EFF]/15 text-[#8db2ff] text-[9px] font-mono-alt uppercase tracking-widest align-middle" data-testid={`service-web-${r.id}`}>Website</span>
                        )}
                        {!r.active && (
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-white/10 text-white/40 text-[9px] font-mono-alt uppercase tracking-widest align-middle" data-testid={`service-hidden-${r.id}`}>Hidden</span>
                        )}
                      </div>
                      <div className="font-display text-xl text-[#8db2ff] tabular-nums whitespace-nowrap">${r.price}</div>
                    </div>
                    {r.description && <div className="text-white/50 text-xs mb-3 line-clamp-2">{r.description}</div>}
                    <div className="flex items-center justify-between text-[10px] font-mono-alt uppercase tracking-widest text-white/40">
                      <span>{Math.round(r.duration_min / 60 * 10) / 10} h</span>
                      <span>{r.taxable ? "TAXABLE" : "NON-TAX"}</span>
                    </div>
                    {canManage && (
                      <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-end gap-1">
                        <button onClick={() => toggle(r)} data-testid={`service-toggle-${r.id}`}
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] transition-colors ${r.active ? "text-white/40 hover:text-amber-300 hover:bg-amber-500/10" : "text-emerald-400/70 hover:text-emerald-300 hover:bg-emerald-500/10"}`}
                          title={r.active ? "Hide service" : "Show service"}>
                          {r.active ? <><EyeOff className="w-3.5 h-3.5" /> Hide</> : <><Eye className="w-3.5 h-3.5" /> Show</>}
                        </button>
                        <button onClick={() => setEditing(r.id)} data-testid={`service-edit-${r.id}`}
                          className="text-white/40 hover:text-white text-[11px] px-2 py-1">Edit</button>
                        <button onClick={() => remove(r.id)} data-testid={`service-delete-${r.id}`}
                          className="p-1.5 rounded text-white/40 hover:text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

function EditRow({ initial, onCancel, onSaved }) {
  const [f, setF] = useState({
    name: initial?.name || "",
    category: initial?.category || CATEGORIES[0],
    description: initial?.description || "",
    price: initial?.price ?? 0,
    duration_min: initial?.duration_min ?? 60,
    taxable: initial?.taxable ?? true,
    active: initial?.active ?? true,
    default_materials: initial?.default_materials || [],
  });
  const [invItems, setInvItems] = useState([]);
  const [matId, setMatId] = useState("");
  const [matQty, setMatQty] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    CRM_API.get("/inventory/items").then(({ data }) => setInvItems(data)).catch(() => {});
  }, []);

  const addMat = () => {
    const q = Number(matQty);
    if (!matId || !q || q <= 0) return;
    const item = invItems.find((i) => i.id === matId);
    set("default_materials", [
      ...f.default_materials.filter((m) => m.item_id !== matId),
      { item_id: matId, qty: q, name: item?.name, unit: item?.unit },
    ]);
    setMatId(""); setMatQty("");
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...f,
        price: Number(f.price),
        duration_min: Number(f.duration_min),
        default_materials: f.default_materials.map(({ item_id, qty }) => ({ item_id, qty: Number(qty) })),
      };
      if (initial) await CRM_API.patch(`/services/${initial.id}`, payload);
      else await CRM_API.post("/services", payload);
      toast.success(initial ? "Updated" : "Added");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 rounded-xl bg-[#1F5EFF]/5 border border-[#1F5EFF]/25 space-y-2" data-testid="service-edit-form">
      <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Name" className={inp} data-testid="edit-service-name" />
      <select value={f.category} onChange={(e) => set("category", e.target.value)} className={inp} data-testid="edit-service-category">
        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <textarea value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={2} className={inp} data-testid="edit-service-desc" />
      <div className="grid grid-cols-2 gap-2">
        <input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} placeholder="Price CAD" className={inp} data-testid="edit-service-price" />
        <input type="number" value={f.duration_min} onChange={(e) => set("duration_min", e.target.value)} placeholder="Duration (min)" className={inp} data-testid="edit-service-duration" />
      </div>
      <div className="flex items-center gap-4 pt-1">
        <label className="flex items-center gap-1.5 text-xs text-white/70">
          <input type="checkbox" checked={f.taxable} onChange={(e) => set("taxable", e.target.checked)} className="w-3.5 h-3.5 accent-[#1F5EFF]" /> Taxable
        </label>
        <label className="flex items-center gap-1.5 text-xs text-white/70">
          <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="w-3.5 h-3.5 accent-[#1F5EFF]" /> Active
        </label>
      </div>

      {/* Default material consumption (auto-attached to work orders) */}
      <div className="pt-2 border-t border-white/10">
        <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest mb-2">
          Material consumption per job
        </div>
        {f.default_materials.length > 0 && (
          <div className="space-y-1 mb-2">
            {f.default_materials.map((m) => (
              <div key={m.item_id} className="flex items-center justify-between text-xs bg-white/5 rounded-lg px-2.5 py-1.5" data-testid={`svc-mat-${m.item_id}`}>
                <span className="text-white/80">{m.name || m.item_id}</span>
                <span className="flex items-center gap-2">
                  <span className="text-[#8db2ff] font-mono-alt">{m.qty} {m.unit || ""}</span>
                  <button type="button" onClick={() => set("default_materials", f.default_materials.filter((x) => x.item_id !== m.item_id))}
                    className="text-white/30 hover:text-red-400" data-testid={`svc-mat-del-${m.item_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <select value={matId} onChange={(e) => setMatId(e.target.value)} className={`${inp} flex-1`} data-testid="svc-mat-select">
            <option value="">Material…</option>
            {invItems.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>)}
          </select>
          <input type="number" min="0" step="any" value={matQty} onChange={(e) => setMatQty(e.target.value)} placeholder="Qty"
            className={`${inp} w-20`} data-testid="svc-mat-qty" />
          <button type="button" onClick={addMat} className="px-2.5 py-2 rounded-lg bg-white/10 text-white/70 hover:bg-[#1F5EFF] hover:text-white text-xs" data-testid="svc-mat-add">
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button onClick={save} disabled={busy} className="flex-1 py-2 rounded-lg bg-[#1F5EFF] text-white text-sm font-medium hover:bg-white hover:text-black disabled:opacity-50" data-testid="edit-service-save">
          {busy ? "…" : "Save"}
        </button>
        <button onClick={onCancel} className="px-3 py-2 text-white/50 text-sm hover:text-white" data-testid="edit-service-cancel">Cancel</button>
      </div>
    </div>
  );
}
